/**
 * Custom injection rule for mobile inline ads used by the ad density experiment.
 *
 * Experiment behaviour:
 * - First ad: inject between two paragraph blocks once the first word threshold is met
 * - Second ad: inject before the next H2 block, but only when there are at least MIN_WORDS_BEFORE_FIRST_HEADING_AD words
 *   (and the configured words-between-ads threshold, if larger) since the previous ad
 * - Subsequent ads: inject before H2 blocks when the configured spacing between ads is met
 *
 * This override replaces the default condition for the `mobile-inline-ads` rule coming from
 * `@carwow/inject-mobile-ads`.
 */

const MIN_WORDS_BEFORE_FIRST_HEADING_AD = 100;

/**
 * Extracts ad-related configuration from the injection options.
 * Falls back to an empty object when ad settings are missing.
 *
 * @param {{ props?: { adSettings?: Object } }} options - Injection rule options.
 * @returns {Object} The ad settings object (possibly empty).
 */
const getAdSettings = ({ props }) => props?.adSettings ?? {};

/**
 * Retrieves the running word count for the current slot from options.
 *
 * @param {Object} options - Injection rule options containing word count data.
 * @returns {number} The number of words associated with the current rule component.
 */
const getWordCountForSlot = options => options?.currentRuleComponentTypeWordCount;

/**
 * Reads the configured threshold for the first mobile ad injection.
 *
 * @param {Object} options - Injection rule options containing ad settings.
 * @returns {number} The number of words required before inserting the first ad.
 */
const getFirstInjectionThreshold = options => getAdSettings(options).numberOfWordsForFirstInjectionMobile;

/**
 * Reads the configured spacing threshold between mobile ads.
 *
 * @param {Object} options - Injection rule options containing ad settings.
 * @returns {number} Minimum words required between consecutive ads.
 */
const getSpacingThreshold = options => getAdSettings(options).numberOfWordsBetweenInlineMobileAds;

// Only TEXT blocks are considered paragraphs in the current content model.
/**
 * Checks whether a body block should be treated as a paragraph.
 *
 * @param {Object} block - A body element from article content.
 * @returns {boolean} True when the block represents a paragraph.
 */
const isParagraphBlock = block => block?.type === 'TEXT';

/**
 * Determines whether the current index sits between two paragraph blocks.
 *
 * @param {Object} options - Injection rule options.
 * @param {Array} options.bodyElements - List of content blocks.
 * @param {number} options.bodyIndex - Current insertion index.
 * @returns {boolean} True if both surrounding blocks are paragraphs.
 */
const isBetweenParagraphs = (options) => {
  const { bodyElements = [], bodyIndex } = options;

  if (!Array.isArray(bodyElements) || typeof bodyIndex !== 'number') return false;

  const previousBlock = bodyElements[bodyIndex];
  const nextBlock = bodyElements[bodyIndex + 1];

  return isParagraphBlock(previousBlock) && isParagraphBlock(nextBlock);
};

/**
 * Validates that the current word count satisfies the supplied threshold.
 *
 * @param {Object} options - Injection rule options.
 * @param {number} threshold - Minimum number of words required.
 * @returns {boolean} True when the accumulated words meet the threshold.
 */
const hasRequiredWordCount = (options, threshold) => getWordCountForSlot(options) >= threshold;

/**
 * Calculates injection eligibility for the first ad placement.
 *
 * @param {Object} options - Injection rule options.
 * @returns {boolean} True when the first ad can be injected between paragraphs.
 */
const shouldInjectFirstAd = options => isBetweenParagraphs(options)
  && hasRequiredWordCount(options, getFirstInjectionThreshold(options));

/**
 * Block types that always render as H2 headings.
 * Add new block types here if they render with H2-level headings.
 * @type {string[]}
 */
const H2_BLOCK_TYPES = [
  'REVIEW',
  'AtAGlance',
  'CHECKLIST_BLOCK',
];

/**
 * PlaceholderBlock variants that render with H2 titles.
 * Add new placeholder types here if they render with H2-level headings.
 * @type {string[]}
 */
const H2_PLACEHOLDER_TYPES = [
  'NUMBERED_LIST_BLOCK',
  'LIST_BLOCK',
];


/**
 * Type checkers for determining H2-level blocks
 * @type {{
 *   HEADER: function(Object): boolean - Checks headers with data property,
 *   PlaceholderBlock: function(Object): boolean - Checks placeholders with placeholder property
 * }}
 */
const typeCheckers = {
  HEADER: (nextElem) => {
    if (!nextElem) return false;
    const data = typeof nextElem.data === 'string' ? JSON.parse(nextElem.data || '{}') : nextElem.data;
    return Number(data.size) === 2;
  },

  PlaceholderBlock: nextElem => H2_PLACEHOLDER_TYPES.includes(nextElem?.placeholder),
};

/**
 * Determines if the current position is immediately before an H2 heading.
 * Checks for:
 * - HEADER blocks with size 2
 * - Block types in H2_BLOCK_TYPES array
 * - PlaceholderBlock types in H2_PLACEHOLDER_TYPES array
 *
 * @param {Object} options - Injection options
 * @param {Array} options.bodyElements - Array of body content blocks
 * @param {number} options.bodyIndex - Current index in body array
 * @param {string} options.nextType - Type of the next block
 * @returns {boolean} True if next block is an H2-level heading
 */
const isBeforeHeading = (options) => {
  const { bodyElements, bodyIndex, nextType } = options;

  if (!nextType) return false;
  if (H2_BLOCK_TYPES.includes(nextType)) return true;

  const nextElem = bodyElements[bodyIndex + 1];
  return typeCheckers[nextType]?.(nextElem) || false;
};

/**
 * Calculates injection eligibility for the second ad placement.
 *
 * @param {Object} options - Injection rule options.
 * @returns {boolean} True when the second ad can be injected before an H2 block.
 */
const shouldInjectSecondAd = options => isBeforeHeading(options)
  && hasRequiredWordCount(options, Math.max(
    MIN_WORDS_BEFORE_FIRST_HEADING_AD,
    getSpacingThreshold(options),
  ));

/**
 * Calculates injection eligibility for ads beyond the second placement.
 *
 * @param {Object} options - Injection rule options.
 * @returns {boolean} True when another ad can be injected before an H2 block.
 */
const shouldInjectSubsequentAd = options => isBeforeHeading(options)
  && hasRequiredWordCount(options, getSpacingThreshold(options));

/**
 * Main injection condition function that overrides the default mobile ads condition.
 *
 * Injection logic summary:
 * - First ad (adCount === 0): inject between paragraph blocks at the configured first-ad threshold
 * - Second ad (adCount === 1): inject before the next H2 once at least MIN_WORDS_BEFORE_FIRST_HEADING_AD (or configured spacing) words have passed
 * - Further ads: inject before H2 blocks respecting numberOfWordsBetweenInlineMobileAds
 *
 * @param {Object} options - Injection options containing body elements, index, and ad settings
 * @returns {boolean} True if ad should be injected at current position
 */
const shouldInject = (options) => {
  const adCount = options?.currentRuleInjectedComponentCount;

  if (adCount === 0) {
    return shouldInjectFirstAd(options);
  }

  if (adCount === 1) {
    return shouldInjectSecondAd(options);
  }

  return shouldInjectSubsequentAd(options);
};

export default shouldInject;
